import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUpsertOperation } from '@/hooks/queries/useOperations';
import { useTiers } from '@/hooks/queries/useTiers';
import { useAllActivites } from '@/hooks/queries/useActivites';
import type {
  OperationCategorie,
  OperationRow,
  OperationSens,
  OperationStatut,
} from '@/lib/database.types';
import { cn } from '@/lib/utils';

// Sprint 6.9.9 — Modal d'édition d'une opération existante.
// Léger (vs OperationCaisseFormDialog 829 LOC qui gère la création complète).
// Édite les champs métier modifiables sans changer la cible (caisse/compte) ni le canal
// pour préserver la cohérence comptable (utiliser delete + recreate sinon).

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  operation: OperationRow | null;
}

const SENS_OPTIONS: { value: OperationSens; label: string }[] = [
  { value: 'entree', label: 'Entrée' },
  { value: 'sortie', label: 'Sortie' },
];

const STATUT_OPTIONS: { value: OperationStatut; label: string }[] = [
  { value: 'brouillon', label: 'Brouillon' },
  { value: 'en_attente', label: 'En attente' },
  { value: 'validee', label: 'Validée' },
  { value: 'approuvee', label: 'Approuvée' },
];

const CATEGORIE_OPTIONS: { value: OperationCategorie; label: string }[] = [
  { value: 'vente_service', label: 'Vente service' },
  { value: 'vente_marchandise', label: 'Vente marchandise' },
  { value: 'remboursement', label: 'Remboursement' },
  { value: 'achat_marchandise', label: 'Achat marchandise' },
  { value: 'sous_traitance', label: 'Sous-traitance' },
  { value: 'salaire', label: 'Salaire' },
  { value: 'charge_sociale', label: 'Charge sociale' },
  { value: 'loyer', label: 'Loyer' },
  { value: 'electricite', label: 'Électricité' },
  { value: 'telecom', label: 'Télécom' },
  { value: 'transport', label: 'Transport' },
  { value: 'fournitures', label: 'Fournitures' },
  { value: 'commission', label: 'Commission' },
  { value: 'taxe', label: 'Taxe' },
  { value: 'remise', label: 'Remise' },
  { value: 'transfert_interne', label: 'Transfert interne' },
  { value: 'avance_personnel', label: 'Avance personnel' },
  { value: 'autre', label: 'Autre' },
];

function fmtAmount(n: number | ''): string {
  if (n === '' || n === 0) return '';
  return n.toLocaleString('fr-FR', { maximumFractionDigits: 0 });
}

function parseAmount(s: string): number {
  const cleaned = s.replace(/[^\d-]/g, '');
  if (!cleaned || cleaned === '-') return 0;
  const n = Number(cleaned);
  return isNaN(n) ? 0 : n;
}

export function EditOperationDialog({ open, onOpenChange, operation }: Props): JSX.Element {
  const upsert = useUpsertOperation();
  const { data: tiers = [] } = useTiers({
    entrepriseId: operation?.entreprise_id ?? null,
    search: '',
  });
  const { data: allActivites = [] } = useAllActivites();
  const activitesScoped = allActivites.filter((a) => a.entreprise_id === operation?.entreprise_id);

  const [dateOp, setDateOp] = useState('');
  const [sens, setSens] = useState<OperationSens>('entree');
  const [categorie, setCategorie] = useState<OperationCategorie>('autre');
  const [statut, setStatut] = useState<OperationStatut>('brouillon');
  const [montant, setMontant] = useState<number | ''>('');
  const [montantDisplay, setMontantDisplay] = useState('');
  const [motif, setMotif] = useState('');
  const [observation, setObservation] = useState('');
  const [tierId, setTierId] = useState('');
  const [activiteId, setActiviteId] = useState('');
  const [reference, setReference] = useState('');

  useEffect(() => {
    if (!open || !operation) return;
    setDateOp(operation.date_operation.slice(0, 10));
    setSens(operation.sens);
    setCategorie(operation.categorie);
    setStatut(operation.statut);
    setMontant(Number(operation.montant));
    setMontantDisplay(fmtAmount(Number(operation.montant)));
    setMotif(operation.motif ?? '');
    setObservation(operation.observation ?? '');
    setTierId(operation.tier_id ?? '');
    setActiviteId(operation.activite_id ?? '');
    setReference(operation.reference ?? '');
  }, [open, operation]);

  if (!operation) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent />
      </Dialog>
    );
  }

  const submit = async (): Promise<void> => {
    if (!motif.trim()) return toast.error('Motif obligatoire') as never;
    const m = typeof montant === 'number' ? montant : 0;
    if (m <= 0) return toast.error('Montant > 0 obligatoire') as never;
    try {
      await upsert.mutateAsync({
        id: operation.id,
        entreprise_id: operation.entreprise_id,
        date_operation: `${dateOp}T${operation.date_operation.slice(11, 19) || '12:00:00'}`,
        sens,
        canal: operation.canal,
        categorie,
        caisse_id: operation.caisse_id ?? null,
        compte_bancaire_id: operation.compte_bancaire_id ?? null,
        tier_id: tierId || null,
        activite_id: activiteId || null,
        activite_beneficiaire_id: activiteId || null,
        activite_emettrice_id: activiteId || null,
        montant: m,
        montant_dzd: m,
        taux_change: Number(operation.taux_change ?? 1),
        devise_code: operation.devise_code ?? 'DZD',
        motif: motif.trim().slice(0, 500),
        observation: observation.trim() || null,
        statut,
      } as never);
      toast.success('Opération mise à jour');
      onOpenChange(false);
    } catch (e) {
      toast.error(`Échec : ${(e as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Modifier l&apos;opération · {operation.reference ?? operation.id.slice(0, 8)}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Canal (<strong>{operation.canal}</strong>) et cible (caisse/compte) non modifiables ici
            pour préserver la cohérence comptable. Pour les changer : supprimez puis recréez.
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Date">
              <input
                type="date"
                value={dateOp}
                onChange={(e) => setDateOp(e.target.value)}
                className="input"
                required
              />
            </Field>
            <Field label="Référence">
              <input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="input font-mono"
                readOnly
              />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Sens">
              <select
                value={sens}
                onChange={(e) => setSens(e.target.value as OperationSens)}
                className="input"
              >
                {SENS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Catégorie">
              <select
                value={categorie}
                onChange={(e) => setCategorie(e.target.value as OperationCategorie)}
                className="input"
              >
                {CATEGORIE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Statut">
              <select
                value={statut}
                onChange={(e) => setStatut(e.target.value as OperationStatut)}
                className="input"
              >
                {STATUT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Montant (DA)" required>
            <input
              value={montantDisplay}
              onChange={(e) => {
                const n = parseAmount(e.target.value);
                setMontant(n === 0 ? '' : n);
                setMontantDisplay(e.target.value.replace(/[^\d ,.-]/g, ''));
              }}
              onBlur={() => montant !== '' && setMontantDisplay(fmtAmount(montant))}
              className="input tabular-nums"
              inputMode="numeric"
              required
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Tiers">
              <select value={tierId} onChange={(e) => setTierId(e.target.value)} className="input">
                <option value="">— Aucun —</option>
                {tiers.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.raison_sociale}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Activité">
              <select
                value={activiteId}
                onChange={(e) => setActiviteId(e.target.value)}
                className="input"
              >
                <option value="">— Aucune —</option>
                {activitesScoped.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.nom}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Motif" required>
            <input
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              className="input"
              required
              maxLength={500}
            />
          </Field>

          <Field label="Observation">
            <textarea
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              className="input min-h-[60px]"
              rows={2}
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={upsert.isPending}
            className={cn(
              'inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-indigo-700',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            {upsert.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Enregistrer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-700">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </span>
      {children}
    </label>
  );
}
